import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import StatusBadge from "../components/StatusBadge";
import NotificationsBell from "../components/NotificationsBell";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { ArrowLeft, Check, X, MessageCircle, LogOut, Circle, CheckCircle2, Loader2, FileText, Wallet, Paperclip, Lock, PlayCircle, ChevronDown, ChevronRight, RotateCcw, Image as ImageIcon, ClipboardCheck, CalendarDays, Bell, ClipboardList, Sparkles, MessageSquare, AlertTriangle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "../components/ui/dialog";
import DocumentsTab from "../components/DocumentsTab";
import ProjectFeed from "../components/ProjectFeed";
import PaymentStageDocuments from "../components/PaymentStageDocuments";
import VOAttachments from "../components/VOAttachments";
import VOTimeline from "../components/VOTimeline";
import DefectsPanel, { countActionable as countActionableDefects } from "../components/DefectsPanel";

export default function CustomerProject() {
  const { projectId } = useParams();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t: T } = useTranslation(["customer", "common"]);
  const [sp, setSp] = useSearchParams();
  const currentTab = sp.get("tab") || "action";
  const [project, setProject] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [vos, setVos] = useState([]);
  const [feed, setFeed] = useState([]);
  const [paymentStages, setPaymentStages] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [defects, setDefects] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    const [p, v, f, ps, t, df] = await Promise.all([
      api.get(`/projects/${projectId}`),
      api.get(`/projects/${projectId}/vos`),
      api.get(`/projects/${projectId}/feed`),
      api.get(`/projects/${projectId}/payment-stages`).catch(() => ({ data: { stages: [] } })),
      api.get(`/projects/${projectId}/tasks`).catch(() => ({ data: { tasks: [] } })),
      api.get(`/projects/${projectId}/defects`).catch(() => ({ data: { defects: [] } })),
    ]);
    setProject(p.data.project);
    setMilestones(p.data.milestones || []);
    setVos(v.data.vos || []);
    setFeed(f.data.feed || []);
    setPaymentStages(ps.data.stages || []);
    setTasks(t.data.tasks || []);
    setDefects(df.data.defects || []);
    setLoading(false);
  }, [projectId]);

  useEffect(() => { reload(); }, [reload]);

  const doLogout = async () => { await logout(); navigate("/"); };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#7A7571]">{T("customer:project.loading")}</div>;
  if (!project) return <div className="min-h-screen flex items-center justify-center text-[#7A7571]">{T("customer:project.notFound")}</div>;

  // Task-based progress (mirrors ID firm's Project Hub). A task counts as done
  // when it's `done` OR approved by the ID firm and forwarded to the customer.
  const doneTasks = tasks.filter((t) => t.status === "done" || (t.status === "waiting_on_customer" && !!t.approved_at)).length;
  const progressPct = tasks.length > 0 ? Math.round((doneTasks / tasks.length) * 100) : 0;
  const pendingVOs = vos.filter((v) => v.status === "pending");
  const revisionVOs = vos.filter((v) => v.status === "revision_requested");
  const previousVOs = vos.filter((v) => v.status === "approved" || v.status === "rejected");
  const awaitingSignoffTasks = tasks.filter(
    (t) => t.status === "waiting_on_customer" && !!t.approved_at && t.customer_decision !== "approved"
  );
  // Invoices the ID firm has sent that the homeowner hasn't paid yet — the
  // "task reminder" we surface on the Action Required tab.
  const pendingInvoices = paymentStages.filter(
    (s) => s.invoice_sent_at && s.status !== "paid"
  );
  const actionCount = pendingVOs.length + awaitingSignoffTasks.length + pendingInvoices.length;

  const paidStages = paymentStages.filter((s) => s.status === "paid").length;
  const invoicedStages = paymentStages.filter((s) => s.invoice_sent_at || s.status === "invoiced").length;

  const startDate = project.start_date
    ? new Date(project.start_date).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })
    : T("customer:project.notSet");
  const targetEnd = project.target_end_date
    ? new Date(project.target_end_date).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })
    : T("customer:project.notSet");

  const TABS = [
    { key: "action", label: T("customer:project.tabs.action"), icon: <Bell size={14} />, badge: actionCount },
    { key: "payments", label: T("customer:project.tabs.payments"), icon: <Wallet size={14} />, badge: paymentStages.length },
    { key: "defects", label: T("customer:project.tabs.defects"), icon: <AlertTriangle size={14} />, badge: countActionableDefects(defects, "customer"), highlight: true },
    { key: "vo", label: T("customer:project.tabs.vo"), icon: <Sparkles size={14} />, badge: vos.length },
    { key: "hub", label: T("customer:project.tabs.hub"), icon: <ClipboardList size={14} />, badge: tasks.length },
    { key: "updates", label: T("customer:project.tabs.updates"), icon: <MessageSquare size={14} />, badge: feed.length },
    { key: "documents", label: T("customer:project.tabs.documents"), icon: <FileText size={14} />, badge: null },
  ];

  const setTab = (key) => setSp({ tab: key }, { replace: true });

  return (
    <div className="min-h-screen bg-[#FDFBF7] pb-24">
      <header className="border-b border-[#EBE5D9] sticky top-0 z-30 bg-[#FDFBF7]/95 backdrop-blur">
        <div className="max-w-4xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/customer" className="inline-flex items-center gap-2 text-[#7A7571] hover:text-[#2C2A29]">
            <ArrowLeft size={16} /> {T("customer:project.backToProjects")}
          </Link>
          <div className="flex items-center gap-2">
            <LanguageSwitcher variant="header" />
            <NotificationsBell />
            <button onClick={doLogout} data-testid="logout-btn" className="btn-ghost !p-2"><LogOut size={16} /></button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571]">{T("customer:project.title")}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#2C2A29] mt-1">{project.name}</h1>
          <div className="text-[#7A7571] mt-1">{project.address}</div>
        </div>

        {/* Combined snapshot — progress + cost + dates in one glance card */}
        <div className="card-soft" data-testid="cust-overview-card">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Progress + dates */}
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571]">{T("customer:project.progress")}</div>
                  <div className="text-sm font-semibold text-[#2C2A29]" data-testid="cust-progress-pct">{progressPct}%</div>
                </div>
                <div className="h-3 rounded-full bg-[#F7F4EC] overflow-hidden">
                  <div className="h-full bg-[#81B29A] rounded-full transition-all" style={{ width: `${progressPct}%` }} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-2xl bg-[#F7F4EC] p-3">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571] flex items-center gap-1">
                    <CalendarDays size={11} /> {T("customer:project.startDate")}
                  </div>
                  <div className="text-sm font-semibold text-[#2C2A29] mt-1" data-testid="cust-start-date">{startDate}</div>
                </div>
                <div className="rounded-2xl bg-[#F7F4EC] p-3">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571] flex items-center gap-1">
                    <CalendarDays size={11} /> {T("customer:project.targetEnd")}
                  </div>
                  <div className="text-sm font-semibold text-[#2C2A29] mt-1" data-testid="cust-target-end">{targetEnd}</div>
                </div>
              </div>
            </div>

            {/* Cost roll-up */}
            <div className="space-y-3 sm:border-l sm:border-[#EBE5D9] sm:pl-6">
              <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571]">{T("customer:project.cost")}</div>
              <div>
                <div className="text-xs text-[#7A7571]">{T("customer:project.originalContract")}</div>
                <div className="text-lg font-display font-bold text-[#2C2A29]" data-testid="cust-original-cost">
                  ${Number(project.original_contract_value || 0).toLocaleString()}
                </div>
              </div>
              <div>
                <div className="text-xs text-[#7A7571]">{T("customer:project.currentTotal")}</div>
                <div className="text-2xl font-display font-bold text-[#E07A5F]" data-testid="cust-current-cost">
                  ${Number(project.current_project_value || 0).toLocaleString()}
                </div>
              </div>
              {paymentStages.length > 0 && (
                <div className="flex items-center gap-2 pt-1">
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full bg-[#E0F2FE] text-[#075985] border border-[#7DD3FC]">
                    <Paperclip size={10} /> {T("customer:project.invoicedBadge", { count: invoicedStages })}
                  </span>
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full bg-[#DCFCE7] text-[#166534] border border-[#86EFAC]">
                    <CheckCircle2 size={10} /> {T("customer:project.paidBadge", { count: paidStages })}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-6 border-b border-[#EBE5D9] overflow-x-auto hide-scrollbar" role="tablist">
          {TABS.map((tab) => {
            const active = currentTab === tab.key;
            return (
              <button
                key={tab.key}
                data-testid={`cust-tab-${tab.key}`}
                onClick={() => setTab(tab.key)}
                className={`relative pb-3 pt-1 flex items-center gap-2 text-sm font-semibold whitespace-nowrap transition-colors ${
                  active ? "text-[#2C2A29]" : "text-[#7A7571] hover:text-[#2C2A29]"
                }`}
              >
                {tab.icon} {tab.label}
                {tab.badge !== null && tab.badge !== undefined && tab.badge > 0 && (
                  <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full ${
                    tab.key === "action" ? "bg-[#FED7AA] text-[#7C2D12]" : "bg-[#F7F4EC] text-[#7A7571]"
                  }`}>
                    {tab.badge}
                  </span>
                )}
                {active && <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-[#E07A5F] rounded-full" />}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        {currentTab === "action" && (
          <CustomerActionTab
            pendingVOs={pendingVOs}
            revisionVOs={revisionVOs}
            awaitingTasks={awaitingSignoffTasks}
            tasks={tasks}
            milestones={milestones}
            pendingInvoices={pendingInvoices}
            onGoToPayments={() => setTab("payments")}
            onChange={reload}
          />
        )}
        {currentTab === "payments" && (
          paymentStages.length > 0
            ? <CustomerPayments stages={paymentStages} milestones={milestones} onChange={reload} />
            : <EmptyTab icon={<Wallet size={22} />} title={T("customer:project.empty.noPaymentStagesTitle")} body={T("customer:project.empty.noPaymentStagesBody")} />
        )}
        {currentTab === "hub" && (
          tasks.length > 0
            ? <CustomerProjectHub tasks={tasks} milestones={milestones} onChange={reload} />
            : <EmptyTab icon={<ClipboardList size={22} />} title={T("customer:project.empty.noTasksTitle")} body={T("customer:project.empty.noTasksBody")} />
        )}
        {currentTab === "vo" && (
          <CustomerVOTab
            pendingVOs={pendingVOs}
            revisionVOs={revisionVOs}
            previousVOs={previousVOs}
            onChange={reload}
          />
        )}
        {currentTab === "defects" && (
          <DefectsPanel projectId={project.project_id} viewer="customer" onExternalChange={reload} />
        )}
        {currentTab === "updates" && (
          <div data-testid="cust-updates">
            <ProjectFeed projectId={project.project_id} canPost={true} />
          </div>
        )}
        {currentTab === "documents" && (
          <div data-testid="cust-documents">
            <DocumentsTab projectId={project.project_id} canManage={false} isCustomer={true} />
          </div>
        )}
      </main>
    </div>
  );
}

/* ------------------ Action Required tab ------------------ */
function CustomerActionTab({ pendingVOs, revisionVOs, awaitingTasks, tasks, milestones, pendingInvoices = [], onGoToPayments, onChange }) {
  const hasAnything =
    pendingVOs.length > 0
    || awaitingTasks.length > 0
    || revisionVOs.length > 0
    || pendingInvoices.length > 0;
  if (!hasAnything) {
    return (
      <EmptyTab
        icon={<CheckCircle2 size={22} className="text-[#166534]" />}
        title="You're all caught up"
        body="Nothing needs your decision right now. We'll ping you the moment something does."
      />
    );
  }
  return (
    <div className="space-y-6" data-testid="cust-action-tab">
      {pendingInvoices.length > 0 && (
        <div>
          <h2 className="font-display text-xl font-semibold text-[#2C2A29] mb-3">Payments due</h2>
          <div className="space-y-3">
            {pendingInvoices.map((s) => (
              <InvoiceReminderCard key={s.stage_id} stage={s} milestones={milestones} onGoToPayments={onGoToPayments} onChange={onChange} />
            ))}
          </div>
        </div>
      )}
      {pendingVOs.length > 0 && (
        <div>
          <h2 className="font-display text-xl font-semibold text-[#2C2A29] mb-3">Waiting on your decision</h2>
          <div className="space-y-4">
            {pendingVOs.map((v) => (
              <VOCard key={v.vo_id} vo={v} onDone={onChange} />
            ))}
          </div>
        </div>
      )}
      {awaitingTasks.length > 0 && (
        <div>
          <h2 className="font-display text-xl font-semibold text-[#2C2A29] mb-3">Task sign-offs needed</h2>
          <CustomerProjectHub
            tasks={tasks.filter((t) =>
              awaitingTasks.some((a) => a.task_id === t.task_id)
              || (t.status === "waiting_on_customer" && !!t.approved_at)
            )}
            milestones={milestones}
            onChange={onChange}
          />
        </div>
      )}
      {revisionVOs.length > 0 && (
        <div>
          <h2 className="font-display text-xl font-semibold text-[#2C2A29] mb-2">With your ID firm</h2>
          <p className="text-sm text-[#7A7571] mb-3">You asked for edits. Your ID firm will revise and resend for your review.</p>
          <div className="space-y-4">
            {revisionVOs.map((v) => (
              <VOCard key={v.vo_id} vo={v} onDone={onChange} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* Invoice reminder — surfaced on Action Required tab whenever the ID firm has
   clicked "Send invoice to homeowner" and the stage hasn't been paid yet.
   Shows a day counter: N days until due, N days overdue, or Due today.
   Clicking "View invoice" opens an in-app modal with the stage details and
   documents so the homeowner never loses the Action Required context. */
function InvoiceReminderCard({ stage, milestones = [], onGoToPayments, onChange }) {
  const [open, setOpen] = useState(false);
  const due = stage.due_date ? new Date(stage.due_date) : null;
  let counter = null;
  let overdue = false;
  if (due) {
    // Compare at date granularity (strip time) so "Due today" fires on the day
    // itself and doesn't flicker with the browser's local clock.
    const today = new Date();
    const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate());
    const todayDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const diffMs = dueDay - todayDay;
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays > 0) counter = { text: `Due in ${diffDays} day${diffDays === 1 ? "" : "s"}`, cls: "bg-[#FEF3C7] text-[#92400E] border-[#FCD34D]" };
    else if (diffDays === 0) counter = { text: "Due today", cls: "bg-[#FED7AA] text-[#7C2D12] border-[#F97316] animate-pulse" };
    else {
      overdue = true;
      const n = Math.abs(diffDays);
      counter = { text: `${n} day${n === 1 ? "" : "s"} overdue`, cls: "bg-[#FEE2E2] text-[#991B1B] border-[#FCA5A5] animate-pulse" };
    }
  }
  const invoiceSent = stage.invoice_sent_at
    ? new Date(stage.invoice_sent_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })
    : null;
  return (
    <div
      data-testid={`cust-invoice-reminder-${stage.stage_id}`}
      className={`card-soft !p-5 ${overdue ? "!bg-[#FEF2F2] !border-[#FCA5A5]" : "!bg-[#FEF3C7]/60 !border-[#FCD34D]"}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${overdue ? "bg-[#FEE2E2] text-[#991B1B]" : "bg-[#FEF3C7] text-[#92400E]"}`}>
              {overdue ? <AlertTriangle size={16} /> : <Wallet size={16} />}
            </div>
            <div className="min-w-0">
              <div className="font-display text-lg font-bold text-[#2C2A29] truncate">{stage.label}</div>
              {invoiceSent && (
                <div className="text-[11px] text-[#7A7571]">Invoice sent {invoiceSent}</div>
              )}
            </div>
          </div>
        </div>
        <div className="text-2xl font-display font-bold text-[#E07A5F] whitespace-nowrap">
          ${Number(stage.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </div>
      </div>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        {counter ? (
          <span
            className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest border ${counter.cls}`}
            data-testid={`cust-invoice-counter-${stage.stage_id}`}
          >
            <CalendarDays size={12} /> {counter.text}
          </span>
        ) : (
          <span className="text-xs text-[#7A7571] italic">No due date set</span>
        )}
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid={`cust-invoice-goto-${stage.stage_id}`}
          className="btn-primary !py-2 !px-4 text-xs inline-flex items-center gap-1"
        >
          View invoice
        </button>
      </div>
      <InvoiceDetailDialog
        open={open}
        onOpenChange={setOpen}
        stage={stage}
        milestones={milestones}
        onGoToPayments={onGoToPayments}
        onChange={onChange}
      />
    </div>
  );
}

/* In-app modal that keeps the homeowner on the Action Required tab while
   showing everything they need to pay the stage: label, amount, due date,
   linked milestones (so they know what they're funding), the invoice-sent
   notice, and every document the ID firm attached (invoice PDF, receipts,
   supporting files). A footer link jumps to the full Payment schedule tab
   for context but is optional. */
function InvoiceDetailDialog({ open, onOpenChange, stage, milestones = [], onGoToPayments, onChange }) {
  const { user } = useAuth();
  const linkedIds = stage.linked_milestone_ids || (stage.linked_milestone_id ? [stage.linked_milestone_id] : []);
  const linkedNames = linkedIds
    .map((id) => milestones.find((m) => m.milestone_id === id)?.name)
    .filter(Boolean);
  const invoiceSent = stage.invoice_sent_at
    ? new Date(stage.invoice_sent_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })
    : null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-3xl max-h-[90vh] overflow-y-auto" data-testid={`cust-invoice-dialog-${stage.stage_id}`}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Invoice details</DialogTitle>
          <DialogDescription>
            Everything your ID firm shared for this payment stage.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-2xl bg-[#FBF7EF] border border-[#EBE5D9] p-4">
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571] mb-1">Stage</div>
            <div className="font-display text-lg font-semibold text-[#2C2A29] leading-snug break-words">{stage.label}</div>
            <div className="text-3xl font-display font-bold text-[#E07A5F] mt-2">
              ${Number(stage.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3 text-xs">
              <div>
                <div className="text-[#7A7571]">Due date</div>
                <div className="font-semibold text-[#2C2A29] mt-0.5">
                  {stage.due_date || <span className="italic text-[#7A7571]">Not set</span>}
                </div>
              </div>
              <div>
                <div className="text-[#7A7571]">Invoice sent</div>
                <div className="font-semibold text-[#2C2A29] mt-0.5">
                  {invoiceSent || <span className="italic text-[#7A7571]">Not yet</span>}
                </div>
              </div>
            </div>
            {linkedNames.length > 0 && (
              <div className="mt-3 pt-3 border-t border-[#EBE5D9]">
                <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571] mb-1">Covers milestones</div>
                <div className="flex flex-wrap gap-1.5">
                  {linkedNames.map((n) => (
                    <span key={n} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-white border border-[#EBE5D9] text-[#7A7571]">
                      <Lock size={10} /> {n}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Documents attached to this stage — read-only for homeowners.
              PaymentStageDocuments already hides upload buttons for the
              customer role so this is safe to embed. */}
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571] mb-2">Attached documents</div>
            <PaymentStageDocuments
              stageId={stage.stage_id}
              documents={stage.documents || []}
              currentUserId={user?.user_id}
              currentUserRole={user?.role}
              onChange={onChange}
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-2 pt-1">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="btn-secondary flex-1"
              data-testid={`cust-invoice-dialog-close-${stage.stage_id}`}
            >
              Close
            </button>
            {onGoToPayments && (
              <button
                type="button"
                onClick={() => { onOpenChange(false); onGoToPayments(); }}
                className="btn-primary flex-1 inline-flex items-center justify-center gap-2"
                data-testid={`cust-invoice-dialog-schedule-${stage.stage_id}`}
              >
                Open full schedule
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------ VO tab (full history) ------------------ */
function CustomerVOTab({ pendingVOs, revisionVOs, previousVOs, onChange }) {
  if (pendingVOs.length === 0 && revisionVOs.length === 0 && previousVOs.length === 0) {
    return (
      <EmptyTab
        icon={<Sparkles size={22} />}
        title="No variation orders yet"
        body="If your renovation scope changes, your ID firm will send you a VO here for your sign-off."
      />
    );
  }
  return (
    <div className="space-y-6" data-testid="cust-vo-tab">
      {pendingVOs.length > 0 && (
        <div>
          <h2 className="font-display text-xl font-semibold text-[#2C2A29] mb-3">Waiting on your decision</h2>
          <div className="space-y-4">
            {pendingVOs.map((v) => (
              <VOCard key={v.vo_id} vo={v} onDone={onChange} />
            ))}
          </div>
        </div>
      )}
      {revisionVOs.length > 0 && (
        <div>
          <h2 className="font-display text-xl font-semibold text-[#2C2A29] mb-3">With your ID firm</h2>
          <div className="space-y-4">
            {revisionVOs.map((v) => (
              <VOCard key={v.vo_id} vo={v} onDone={onChange} />
            ))}
          </div>
        </div>
      )}
      {previousVOs.length > 0 && (
        <div>
          <h2 className="font-display text-xl font-semibold text-[#2C2A29] mb-3">Previous VOs</h2>
          <div className="space-y-3">
            {previousVOs.map((v) => (
              <div key={v.vo_id} className="card-soft !p-5" data-testid={`cust-prev-vo-${v.vo_id}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="font-semibold text-[#2C2A29]">{v.title}</div>
                  <StatusBadge status={v.status} />
                </div>
                <div className="text-sm text-[#7A7571]">${Number(v.cost_delta).toLocaleString()} • {new Date(v.responded_at || v.created_at).toLocaleDateString()}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function EmptyTab({ icon, title, body }) {
  return (
    <div className="card-soft text-center py-14" data-testid="cust-empty-tab">
      <div className="w-14 h-14 rounded-full bg-[#F7F4EC] flex items-center justify-center mx-auto mb-4 text-[#7A7571]">
        {icon}
      </div>
      <div className="font-display text-xl font-bold text-[#2C2A29]">{title}</div>
      <p className="text-sm text-[#7A7571] mt-2 max-w-md mx-auto">{body}</p>
    </div>
  );
}

function CustomerPayments({ stages, milestones, onChange }) {
  const [stagesOpen, setStagesOpen] = useState(true);
  const { user } = useAuth();
  const msById = useMemo(() => new Map(milestones.map((m) => [m.milestone_id, m])), [milestones]);
  const summary = useMemo(() => {
    let paid = 0, pending = 0, waived = 0;
    stages.forEach((s) => {
      if (s.status === "paid") paid += s.amount || 0;
      else if (s.status === "waived") waived += s.amount || 0;
      else pending += s.amount || 0;
    });
    const total = paid + pending + waived;
    const collected = total > 0 ? Math.round((paid / total) * 100) : 0;
    return { paid, pending, waived, total, collected };
  }, [stages]);

  // Prefer the most recently invoiced pending stage as the "Next payment due"
  // pointer — that's what the homeowner actually needs to action.
  const upcoming = stages.find((s) => s.status === "pending" && s.invoice_sent_at)
    || stages.find((s) => s.status === "pending");

  const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const statusPill = (status) => {
    if (status === "paid") return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#DCFCE7] text-[#166534]">
        <Check size={11} /> Paid
      </span>
    );
    if (status === "waived") return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#E0E7FF] text-[#3730A3]">
        <PlayCircle size={11} /> Started · Pending
      </span>
    );
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#FEF3C7] text-[#92400E]">
        <Wallet size={11} /> Pending
      </span>
    );
  };

  return (
    <div className="card-soft" data-testid="customer-payments">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571]">Payment schedule</div>
        <div className="text-sm text-[#7A7571]">{summary.collected}% collected</div>
      </div>

      <div className="h-3 rounded-full bg-[#F7F4EC] overflow-hidden mb-4">
        <div className="h-full bg-[#81B29A] rounded-full transition-all" style={{ width: `${summary.collected}%` }} />
      </div>

      <div className="grid grid-cols-3 gap-2 mb-4 text-center">
        <div className="p-2 rounded-2xl bg-[#DCFCE7]">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#166534]">Paid</div>
          <div className="font-semibold text-[#166534] text-sm mt-0.5">{fmt(summary.paid)}</div>
        </div>
        <div className="p-2 rounded-2xl bg-[#FEF3C7]">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#92400E]">Pending</div>
          <div className="font-semibold text-[#92400E] text-sm mt-0.5">{fmt(summary.pending)}</div>
        </div>
        <div className="p-2 rounded-2xl bg-[#F7F4EC]">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571]">Total</div>
          <div className="font-semibold text-[#2C2A29] text-sm mt-0.5">{fmt(summary.total)}</div>
        </div>
      </div>

      {upcoming && (
        <div className="rounded-2xl bg-[#FFFBEB] border border-[#FCD34D] p-3 mb-4 text-sm text-[#92400E]">
          <div className="text-[10px] uppercase tracking-widest font-bold mb-0.5">Next payment due</div>
          <div className="font-semibold">{fmt(upcoming.amount)} · {upcoming.label}</div>
          {upcoming.due_date && <div className="text-xs mt-0.5">Due: {upcoming.due_date}</div>}
        </div>
      )}

      <div>
        <button
          type="button"
          onClick={() => setStagesOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-2 py-2 text-left group"
          data-testid="customer-payments-toggle"
          aria-expanded={stagesOpen}
        >
          <span className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571] group-hover:text-[#2C2A29] transition-colors">
            {stagesOpen ? "Hide payment stages" : `View all payment stages (${stages.length})`}
          </span>
          <ChevronDown
            size={16}
            className={`text-[#7A7571] transition-transform ${stagesOpen ? "rotate-180" : ""}`}
          />
        </button>
        {stagesOpen && (
          <div className="space-y-2 pt-2">
            {stages.map((s, i) => {
              const linkedIds = s.linked_milestone_ids || (s.linked_milestone_id ? [s.linked_milestone_id] : []);
              const linkedNames = linkedIds.map((id) => msById.get(id)?.name).filter(Boolean);
              return (
                <div
                  key={s.stage_id}
                  className="rounded-2xl border border-[#EBE5D9] p-3"
                  data-testid={`customer-payment-${s.stage_id}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571]">Stage {i + 1}</div>
                    {statusPill(s.status)}
                  </div>
                  <div className="text-sm text-[#2C2A29] leading-snug">{s.label}</div>
                  <div className="flex items-center justify-between mt-2 gap-3 flex-wrap">
                    <div className="text-lg font-display font-bold text-[#2C2A29]">{fmt(s.amount)}</div>
                    {linkedNames.length > 0 && (
                      <span className="inline-flex flex-wrap items-center gap-1 text-[11px] text-[#7A7571]">
                        <Lock size={11} className="flex-shrink-0" />
                        {linkedNames.join(" · ")}
                      </span>
                    )}
                  </div>
                  {s.due_date && (
                    <div className="text-[11px] text-[#7A7571] mt-1">Due by <span className="font-semibold text-[#2C2A29]">{s.due_date}</span></div>
                  )}
                  {s.invoice_sent_at && s.status !== "paid" && (
                    <div className="mt-2 rounded-xl bg-[#EEF2FF] text-[#3730A3] text-[11px] p-2 flex items-center gap-2">
                      <ClipboardCheck size={12} />
                      <span>
                        <span className="font-semibold">Invoice issued</span> on {new Date(s.invoice_sent_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                      </span>
                    </div>
                  )}
                  {s.proof_uploaded_at && (
                    <a
                      href={`${api.defaults.baseURL}/payment-stages/${s.stage_id}/proof`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs text-[#166534] hover:underline mt-2"
                    >
                      <Paperclip size={12} /> Legacy proof of payment
                    </a>
                  )}
                  {/* Multi-file document store shared with the ID firm. */}
                  <PaymentStageDocuments
                    stageId={s.stage_id}
                    documents={s.documents || []}
                    currentUserId={user?.user_id}
                    currentUserRole={user?.role}
                    onChange={onChange}
                  />
                  {s.status === "waived" && s.justification && (
                    <div className="mt-2 rounded-xl bg-[#EEF2FF] text-[#3730A3] text-xs p-2">
                      <span className="font-semibold">Started before payment: </span>
                      {s.justification}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function VOCard({ vo, onDone }) {
  const [busy, setBusy] = useState(false);
  const [action, setAction] = useState(null); // 'approve' | 'reject' | 'revision'
  const [note, setNote] = useState("");
  const [showTimeline, setShowTimeline] = useState(false);
  const isRevisionRequested = vo.status === "revision_requested";
  const cost = Number(vo.cost_delta || 0);

  const doAction = async () => {
    if (action === "revision" && !note.trim()) {
      toast.error("Please describe what needs to change");
      return;
    }
    setBusy(true);
    try {
      if (action === "approve") await api.post(`/vos/${vo.vo_id}/approve`, { note });
      else if (action === "reject") await api.post(`/vos/${vo.vo_id}/reject`, { note });
      else if (action === "revision") await api.post(`/vos/${vo.vo_id}/request-revision`, { note });
      const msg = action === "approve" ? "Approved — thank you!"
        : action === "reject" ? "Rejected. Your ID firm will follow up."
          : "Sent back to your ID firm";
      toast.success(msg);
      setAction(null);
      setNote("");
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="card-soft !p-6" data-testid={`vo-approval-card-${vo.vo_id}`}>
      <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-xl font-semibold text-[#2C2A29]">{vo.title}</h3>
          {vo.revision_count > 0 && (
            <div className="text-[10px] uppercase tracking-widest font-semibold text-[#0F766E] mt-1">
              Revised · Round {vo.revision_count + 1}
            </div>
          )}
        </div>
        <StatusBadge status={vo.status} />
      </div>
      <p className="text-[#2C2A29] mb-4 whitespace-pre-wrap">{vo.description}</p>

      {/* Quotation line items */}
      {(vo.line_items || []).length > 0 && (
        <div className="mb-4 rounded-2xl bg-[#FBF7EF]/70 border border-[#EBE5D9] overflow-hidden">
          <div className="px-3 py-2 text-[10px] uppercase tracking-widest font-bold text-[#7A7571] bg-[#F7F4EC]">
            Quotation
          </div>
          <div className="divide-y divide-[#EBE5D9]">
            {vo.line_items.map((li) => (
              <div key={li.item_id} className="flex items-start justify-between gap-2 px-3 py-2 text-sm">
                <div className="min-w-0 flex-1">
                  <div className="text-[#2C2A29] font-medium">{li.description}</div>
                  <div className="text-[11px] text-[#7A7571] mt-0.5">
                    {li.item_code ? `#${li.item_code} · ` : ""}
                    {li.qty}{li.unit ? ` ${li.unit}` : ""} × ${Number(li.unit_price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
                <div className="text-sm font-semibold text-[#2C2A29] whitespace-nowrap">
                  ${Number(li.line_total).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <div className="text-xs text-[#7A7571]">Cost impact</div>
        <div className="text-2xl font-display font-bold text-[#E07A5F]">
          +${cost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </div>
      </div>

      {vo.pay_by_date && (
        <div className="mb-4 flex items-center justify-between rounded-2xl bg-[#FEF3C7] border border-[#FCD34D] px-3 py-2">
          <div className="text-xs uppercase tracking-widest font-bold text-[#92400E]">Pay by</div>
          <div className="text-sm font-semibold text-[#92400E]" data-testid={`vo-pay-by-${vo.vo_id}`}>
            {new Date(vo.pay_by_date).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
          </div>
        </div>
      )}

      {/* Read-only attachments viewer */}
      {(vo.documents || []).length > 0 && (
        <VOAttachments voId={vo.vo_id} documents={vo.documents} canManage={false} />
      )}

      {isRevisionRequested && !action && (
        <div className="mt-4 p-3 rounded-2xl bg-[#E0E7FF] border border-[#A5B4FC] text-sm text-[#3730A3]">
          Your ID firm is revising this VO based on your remarks. You&apos;ll be notified when it&apos;s ready to review again.
        </div>
      )}

      {vo.status === "pending" && !action && (
        <>
          <p className="text-xs text-[#7A7571] mt-4 mb-3 italic text-center">
            Approving adds ${cost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} to your total and can&apos;t be undone.
          </p>
          <div className="grid grid-cols-1 gap-2">
            <button data-testid={`vo-approve-${vo.vo_id}`} onClick={() => setAction("approve")} className="btn-primary inline-flex items-center justify-center gap-2 h-14 rounded-2xl text-base">
              <Check size={18} /> Accept
            </button>
            {/* Stack secondary + danger vertically on narrow phones so long
                labels ("Send back for edits") don't wrap into 3 lines
                against the neighbouring Reject button. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <button data-testid={`vo-revision-${vo.vo_id}`} onClick={() => setAction("revision")} className="btn-secondary inline-flex items-center justify-center gap-2 h-12 rounded-2xl whitespace-nowrap">
                <RotateCcw size={16} className="flex-shrink-0" /> Send back for edits
              </button>
              <button data-testid={`vo-reject-${vo.vo_id}`} onClick={() => setAction("reject")} className="btn-danger inline-flex items-center justify-center gap-2 h-12">
                <X size={16} /> Reject
              </button>
            </div>
          </div>
        </>
      )}
      {action && (
        <div className="space-y-3 mt-4">
          {action === "revision" && (
            <div className="p-3 rounded-2xl bg-[#FFEDD5] border border-[#FDBA74] text-xs text-[#7C2D12]">
              Describe what you&apos;d like changed (scope, pricing, materials, timing…). Your ID firm will revise the quotation and resend.
            </div>
          )}
          {action === "reject" && (
            <div className="p-3 rounded-2xl bg-[#FEE2E2] border border-[#FCA5A5] text-xs text-[#991B1B]">
              Rejecting will lock this VO permanently. Your ID firm will need to raise a fresh VO if they want to try again.
            </div>
          )}
          <textarea className="input-soft min-h-[100px]" data-testid={`vo-note-${vo.vo_id}`}
            placeholder={action === "revision"
              ? "What would you like changed?"
              : action === "reject"
                ? "Reason for rejecting (optional)"
                : "Any note for your designer (optional)"}
            value={note} onChange={(e) => setNote(e.target.value)} />
          <div className="grid grid-cols-2 gap-2">
            <button data-testid={`vo-cancel-${vo.vo_id}`} onClick={() => { setAction(null); setNote(""); }} className="btn-secondary h-12">Cancel</button>
            <button data-testid={`vo-confirm-${vo.vo_id}`} onClick={doAction}
              disabled={busy || (action === "revision" && !note.trim())}
              className={action === "reject" ? "btn-danger h-12" : "btn-primary h-12"}>
              {busy ? "Saving…"
                : action === "approve" ? "Confirm accept"
                  : action === "reject" ? "Confirm reject"
                    : "Send back to ID firm"}
            </button>
          </div>
        </div>
      )}

      {/* Timeline toggle */}
      {(vo.timeline || []).length > 0 && (
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setShowTimeline((v) => !v)}
            className="text-xs text-[#7A7571] hover:text-[#2C2A29] underline underline-offset-2"
            data-testid={`vo-toggle-timeline-${vo.vo_id}`}
          >
            {showTimeline ? "Hide" : "Show"} conversation history ({vo.timeline.length})
          </button>
          {showTimeline && <VOTimeline timeline={vo.timeline} />}
        </div>
      )}
    </div>
  );
}

/* ------------------ Customer Project Hub ------------------ */
function CustomerProjectHub({ tasks, milestones, onChange }) {
  const msById = useMemo(() => new Map(milestones.map((m) => [m.milestone_id, m])), [milestones]);

  // Group tasks by milestone. Preserve firm's milestone order.
  const grouped = useMemo(() => {
    const map = new Map();
    for (const t of tasks) {
      const key = t.milestone_id || "__none__";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(t);
    }
    const orderedIds = [
      ...milestones.map((m) => m.milestone_id).filter((id) => map.has(id)),
      ...(map.has("__none__") ? ["__none__"] : []),
    ];
    return orderedIds.map((id) => ({
      milestone_id: id,
      milestone_name: id === "__none__" ? "Other" : (msById.get(id)?.name || "Milestone"),
      tasks: map.get(id) || [],
    }));
  }, [tasks, milestones, msById]);

  const awaitingCount = tasks.filter((t) => t.status === "waiting_on_customer" && !!t.approved_at && t.customer_decision !== "approved").length;

  return (
    <div className="card-soft" data-testid="customer-project-hub">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571]">Project Hub</div>
          <div className="text-[#7A7571] text-sm mt-0.5">
            {awaitingCount > 0
              ? <><span className="font-semibold text-[#0F766E]">{awaitingCount}</span> task{awaitingCount === 1 ? "" : "s"} awaiting your review</>
              : "All caught up. Your team will ping you when the next task is ready."}
          </div>
        </div>
      </div>

      <div className="space-y-5">
        {grouped.map((g) => (
          <div key={g.milestone_id} data-testid={`cust-hub-milestone-${g.milestone_id}`}>
            <div className="flex items-baseline justify-between mb-2">
              <h3 className="font-display text-lg font-bold text-[#2C2A29]">{g.milestone_name}</h3>
              <span className="text-[10px] uppercase tracking-widest font-semibold text-[#7A7571]">
                {g.tasks.length} task{g.tasks.length === 1 ? "" : "s"}
              </span>
            </div>
            <div className="space-y-2">
              {g.tasks.map((t) => (
                <CustomerTaskCard key={t.task_id} task={t} onChange={onChange} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CustomerTaskCard({ task, onChange }) {
  const awaitingMe = task.status === "waiting_on_customer" && !!task.approved_at && task.customer_decision !== "approved";
  const isDone = task.status === "done";
  const customerRework = task.customer_decision === "rework_requested";

  // Auto-expand tasks that need the homeowner's attention so the photos +
  // approve/rework buttons are visible without an extra click.
  const [reviewOpen, setReviewOpen] = useState(awaitingMe);
  const [reviewImages, setReviewImages] = useState(null);
  const [loadingImages, setLoadingImages] = useState(false);

  // Fetch every visible photo for this task (backend already strips images the
  // ID firm has hidden from the homeowner). We only skip pure text critique
  // entries (rework/customer_rework) since those photos are internal reference
  // notes, not the finished work the homeowner is being asked to approve.
  useEffect(() => {
    if (!reviewOpen || reviewImages !== null || !awaitingMe) return;
    (async () => {
      setLoadingImages(true);
      try {
        const r = await api.get(`/tasks/${task.task_id}/contractor-updates`);
        const list = r.data.updates || [];
        const flat = [];
        for (const u of list) {
          if (u.status_hint === "rework" || u.status_hint === "customer_rework") continue;
          for (const img of (u.images || [])) {
            flat.push({ update_id: u.update_id, image_id: img.image_id });
          }
        }
        setReviewImages(flat);
      } catch (err) {
        setReviewImages([]);
      } finally {
        setLoadingImages(false);
      }
    })();
  }, [reviewOpen, task.task_id, reviewImages, awaitingMe]);

  const pill = (() => {
    if (isDone && task.customer_approved_at) return { text: "You approved this", cls: "bg-[#DCFCE7] text-[#166534] border-[#86EFAC]" };
    if (isDone) return { text: "Completed", cls: "bg-[#DCFCE7] text-[#166534] border-[#86EFAC]" };
    if (awaitingMe) return { text: "Awaiting your review", cls: "bg-[#FED7AA] text-[#7C2D12] border-[#F97316] animate-pulse" };
    if (customerRework) return { text: "Rework in progress", cls: "bg-[#FFEDD5] text-[#9A3412] border-[#FDBA74]" };
    return { text: "In progress with team", cls: "bg-[#F1F5F9] text-[#475569] border-[#CBD5E1]" };
  })();

  // Only awaiting-review tasks are interactive — everything else is a static
  // tile showing title + status. Customer never sees contractor/ID chatter.
  const clickable = awaitingMe;

  return (
    <div
      className={`rounded-2xl bg-[#F7F4EC] overflow-hidden ${clickable ? "" : "cursor-default"}`}
      data-testid={`cust-task-${task.task_id}`}
    >
      <div
        role={clickable ? "button" : undefined}
        onClick={clickable ? () => setReviewOpen((v) => !v) : undefined}
        className={`w-full text-left p-4 ${clickable ? "hover:bg-[#F1EDE3] cursor-pointer" : ""}`}
      >
        {/* Mobile: title on top, pill (+ chevron) below. Desktop: side-by-side. */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="min-w-0 sm:flex-1">
            <div className="font-semibold text-[#2C2A29] break-words">{task.title}</div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap ${pill.cls}`}>
              {pill.text}
            </div>
            {clickable && (
              reviewOpen
                ? <ChevronDown size={16} className="text-[#7A7571]" />
                : <ChevronRight size={16} className="text-[#7A7571]" />
            )}
          </div>
        </div>
      </div>

      {reviewOpen && awaitingMe && (
        <div className="px-4 pb-4 bg-white/50 space-y-3">
          <CustomerReviewActions task={task} onChange={onChange} />
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571] mb-2">Photos for your review</div>
            {loadingImages && <div className="text-sm text-[#7A7571]">Loading photos…</div>}
            {!loadingImages && (reviewImages || []).length === 0 && (
              <div className="text-sm text-[#7A7571]">Your ID firm hasn&apos;t attached photos to this task yet.</div>
            )}
            {!loadingImages && (reviewImages || []).length > 0 && (
              <div className="grid grid-cols-3 gap-2">
                {reviewImages.map((img) => {
                  const src = `${api.defaults.baseURL}/tasks/contractor-updates/${img.update_id}/images/${img.image_id}`;
                  return (
                    <a
                      key={img.image_id}
                      href={src}
                      target="_blank"
                      rel="noreferrer"
                      className="aspect-square rounded-xl overflow-hidden border border-[#EBE5D9] hover:opacity-90"
                    >
                      <img src={src} alt="" className="w-full h-full object-cover" />
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CustomerReviewActions({ task, onChange }) {
  const [approving, setApproving] = useState(false);
  const [reworkOpen, setReworkOpen] = useState(false);
  const [note, setNote] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  const approve = async () => {
    setApproving(true);
    try {
      await api.post(`/tasks/${task.task_id}/customer-approve`);
      toast.success("Approved — thank you!");
      onChange && (await onChange());
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setApproving(false);
    }
  };

  const pickFiles = (e) => {
    const list = Array.from(e.target.files || []);
    if (list.length + files.length > 5) { toast.error("Max 5 photos"); return; }
    setFiles((prev) => [...prev, ...list]);
  };

  const submit = async () => {
    if (!note.trim()) { toast.error("Please share what needs to be redone"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("note", note.trim());
      files.forEach((f) => fd.append("files", f));
      await api.post(`/tasks/${task.task_id}/customer-request-rework`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Sent back to your ID firm");
      setReworkOpen(false);
      setNote("");
      setFiles([]);
      onChange && (await onChange());
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl bg-[#EFF6FF] border border-[#93C5FD] p-3">
      <div className="flex items-start gap-2 mb-2">
        <ClipboardCheck size={16} className="text-[#1E40AF] flex-shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-[#1E40AF] text-sm">Ready for your sign-off</div>
          <div className="text-xs text-[#1E3A8A]">
            Your ID firm has approved this task. Take a look at the photos below and either approve or send it back for rework.
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={approve}
          disabled={approving}
          data-testid={`cust-approve-${task.task_id}`}
          className="btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-60"
        >
          {approving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
          {approving ? "Approving…" : "Approve"}
        </button>
        <button
          type="button"
          onClick={() => setReworkOpen(true)}
          data-testid={`cust-rework-${task.task_id}`}
          className="btn-ghost text-sm inline-flex items-center gap-1.5 border border-[#FDBA74] bg-[#FFEDD5] text-[#9A3412] hover:bg-[#FED7AA]"
        >
          <RotateCcw size={13} /> Request rework
        </button>
      </div>

      <Dialog open={reworkOpen} onOpenChange={setReworkOpen}>
        <DialogContent className="max-w-2xl" data-testid={`cust-rework-dialog-${task.task_id}`}>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Request rework</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-[#7A7571]">
              Share what needs to be redone on <span className="font-semibold text-[#2C2A29]">{task.title}</span>. Your ID firm will review and forward this to the contractor.
            </div>
            <textarea
              className="input-soft mt-1 text-sm min-h-[100px]"
              placeholder="e.g. The paint finish looks uneven near the window frame — could you touch it up?"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid={`cust-rework-note-${task.task_id}`}
            />
            {files.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {files.map((f, i) => (
                  <div key={i} className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-[#F7F4EC] text-xs text-[#2C2A29]">
                    <ImageIcon size={11} />
                    <span className="truncate max-w-[160px]">{f.name}</span>
                    <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} className="text-[#9F1239]"><X size={11} /></button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center justify-between">
              <label className="btn-ghost text-xs inline-flex items-center gap-1 cursor-pointer">
                <Paperclip size={12} /> Attach photo{files.length > 0 ? ` (${files.length})` : ""}
                <input type="file" accept="image/*" multiple className="hidden" onChange={pickFiles} data-testid={`cust-rework-attach-${task.task_id}`} />
              </label>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setReworkOpen(false)} className="btn-ghost text-sm">Cancel</button>
                <button
                  type="button"
                  onClick={submit}
                  disabled={busy || !note.trim()}
                  data-testid={`cust-rework-submit-${task.task_id}`}
                  className="btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-60"
                >
                  {busy ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />}
                  {busy ? "Sending…" : "Send back to ID firm"}
                </button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

